import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, Eye, EyeOff, AlertCircle, ShieldCheck, KeyRound, QrCode, Smartphone, Copy, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import authService, { TwoFactorSetupResponse } from '../services/authService';

interface LoginProps {
  onLogin?: () => void;
}

type LoginStep = 'credentials' | 'verify2fa' | 'setup2fa';

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({ username: '', password: '', rememberMe: false });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 2FA verification state
  const [step, setStep] = useState<LoginStep>('credentials');
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [tfCode, setTfCode] = useState('');

  // 2FA setup state
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null);
  const [setupCode, setSetupCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupSuccess, setSetupSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.username || !formData.password) {
      setError(t('auth.invalidCredentials'));
      return;
    }

    try {
      setLoading(true);
      const result = await authService.login({
        username: formData.username,
        password: formData.password,
        rememberMe: formData.rememberMe,
      });

      if (result.requiresTwoFactor && result.challengeToken) {
        setChallengeToken(result.challengeToken);
        setStep('verify2fa');
        return;
      }

      // Login successful - proceed to dashboard
      // 2FA can be set up from Profile page
      if (onLogin) onLogin();
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!tfCode || tfCode.length !== 6 || !challengeToken) {
      setError(t('auth.twoFactorInvalid'));
      return;
    }

    try {
      setLoading(true);
      await authService.verifyTwoFactor(challengeToken, tfCode);
      if (onLogin) onLogin();
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.twoFactorInvalid'));
      setTfCode('');
    } finally {
      setLoading(false);
    }
  };

  const initiate2FASetup = async () => {
    try {
      setSetupLoading(true);
      const data = await authService.setup2FA();
      setSetupData(data);
      setStep('setup2fa');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to initialize 2FA setup');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleEnable2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!setupCode || setupCode.length !== 6) {
      setError(t('auth.twoFactorInvalid'));
      return;
    }

    try {
      setSetupLoading(true);
      await authService.enable2FA(setupCode);
      setSetupSuccess(true);
      // Brief pause to show success, then navigate
      setTimeout(() => {
        if (onLogin) onLogin();
        navigate('/');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.twoFactorInvalid'));
      setSetupCode('');
    } finally {
      setSetupLoading(false);
    }
  };

  const copySecret = () => {
    if (setupData?.secret) {
      navigator.clipboard.writeText(setupData.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden"
      style={{
        backgroundColor: '#0D1117',
        backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(201,168,76,0.06) 0, transparent 40%), radial-gradient(circle at 75% 75%, rgba(59,130,246,0.05) 0, transparent 40%), radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)',
        backgroundSize: '100% 100%, 100% 100%, 24px 24px'
      }}
    >
      <div className="w-full max-w-md relative z-10">
        {/* Logo + Brand */}
        <div className="flex flex-col items-center mb-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/40">
              <span className="text-white font-black text-xl" style={{ fontFamily: 'Times, "Times New Roman", serif' }}>N</span>
            </div>
            <h1 className="text-3xl font-bold text-white leading-none tracking-tight" style={{ fontFamily: 'Times, "Times New Roman", serif' }}>
              NexVera<span className="text-blue-400 font-black">PDKS</span>
            </h1>
          </div>
          <p className="text-slate-500 text-[10px] font-bold tracking-[0.3em] uppercase mt-2">Personel Devam Kontrol</p>
        </div>

        <div className="bg-slate-800/40 backdrop-blur-xl border border-slate-700/60 rounded-2xl shadow-2xl p-8">
          {/* STEP 1: Login credentials */}
          {step === 'credentials' && (
            <>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white">Hoşgeldiniz</h2>
                <p className="text-slate-400 text-sm mt-1">Devam etmek için giriş yapın</p>
              </div>

              {error && (
                <div className="mb-5 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3 text-red-300">
                  <AlertCircle size={18} />
                  <span className="text-sm font-medium">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] mb-2">
                    {t('auth.username')}
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none text-sm transition-all"
                      placeholder="kullanici_adi"
                      autoComplete="username"
                      autoFocus
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] mb-2">
                    {t('auth.password')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full pl-10 pr-12 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none text-sm transition-all"
                      placeholder="********"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.rememberMe}
                      onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-blue-600 focus:ring-blue-500 focus:ring-offset-slate-800"
                    />
                    <span className="text-sm text-slate-400">{t('auth.rememberMe')}</span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-bold text-sm shadow-lg shadow-blue-500/30 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('auth.loginLoading')}
                    </>
                  ) : (
                    <>
                      <span>&#8594;</span>
                      Giriş Yap
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          {/* STEP 2: Verify existing 2FA code */}
          {step === 'verify2fa' && (
            <>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-blue-500/20 mb-3">
                  <KeyRound size={24} className="text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-white">{t('auth.twoFactorTitle')}</h2>
                <p className="text-sm text-slate-400 mt-1">{t('auth.twoFactorPrompt')}</p>
              </div>

              {error && (
                <div className="mb-5 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3 text-red-300">
                  <AlertCircle size={18} />
                  <span className="text-sm font-medium">{error}</span>
                </div>
              )}

              <form onSubmit={handleVerify2FA} className="space-y-5">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] mb-2">
                    {t('auth.twoFactorCode')}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={tfCode}
                    onChange={(e) => setTfCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none text-2xl text-center font-mono tracking-[0.5em] transition-all"
                    placeholder="000000"
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || tfCode.length !== 6}
                  className="w-full py-3.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-bold text-sm shadow-lg shadow-blue-500/30 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    t('auth.twoFactorVerify')
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => { setStep('credentials'); setChallengeToken(null); setTfCode(''); setError(null); }}
                  className="w-full text-sm text-slate-500 hover:text-slate-300 transition-colors text-center"
                >
                  &larr; {t('auth.login')}
                </button>
              </form>
            </>
          )}

          {/* STEP 3: Mandatory 2FA Setup */}
          {step === 'setup2fa' && (
            <>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-50 mb-3">
                  <Smartphone size={24} className="text-amber-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-800">{t('auth.twoFactorSetup')}</h2>
                <p className="text-sm text-slate-500 mt-1">{t('auth.twoFactorRequired')}</p>
              </div>

              {/* Warning banner */}
              <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3 text-amber-700">
                <ShieldCheck size={18} className="mt-0.5 shrink-0" />
                <span className="text-xs leading-relaxed">{t('auth.twoFactorRequired')}</span>
              </div>

              {error && (
                <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
                  <AlertCircle size={18} />
                  <span className="text-sm font-medium">{error}</span>
                </div>
              )}

              {setupSuccess ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 mb-4">
                    <CheckCircle size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-1">{t('auth.twoFactorEnabled')}</h3>
                  <p className="text-sm text-slate-500">{t('auth.loginLoading')}</p>
                </div>
              ) : setupData ? (
                <form onSubmit={handleEnable2FA} className="space-y-5">
                  {/* QR Code */}
                  <div className="flex flex-col items-center">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
                      {t('auth.scanQrCode')}
                    </p>
                    <div className="p-3 bg-white border-2 border-slate-100 rounded-xl shadow-inner">
                      <img
                        src={setupData.qrCodeUrl}
                        alt="2FA QR Code"
                        className="w-48 h-48"
                        onError={(e) => {
                          // Fallback: use QR code from otpauth URL
                          (e.target as HTMLImageElement).src = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.otpauthUrl)}`;
                        }}
                      />
                    </div>
                  </div>

                  {/* Manual entry secret */}
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                      {t('auth.manualEntry')}
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono text-slate-700 select-all break-all">
                        {setupData.secret}
                      </code>
                      <button
                        type="button"
                        onClick={copySecret}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        title="Copy"
                      >
                        {copied ? <CheckCircle size={18} className="text-green-500" /> : <Copy size={18} />}
                      </button>
                    </div>
                  </div>

                  {/* Verification code input */}
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                      {t('auth.enterVerificationCode')}
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={setupCode}
                      onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none text-2xl text-center font-mono tracking-[0.5em] transition-all"
                      placeholder="000000"
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={setupLoading || setupCode.length !== 6}
                    className="w-full py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-bold text-sm shadow-lg shadow-blue-500/30 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {setupLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <ShieldCheck size={16} />
                        {t('auth.enable2fa')}
                      </>
                    )}
                  </button>
                </form>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </>
          )}
        </div>

        <p className="text-center text-slate-600 text-xs mt-6">
          &copy; {new Date().getFullYear()} NexVera PDKS. T&uuml;m haklar&#305; sakl&#305;d&#305;r.
        </p>
      </div>
    </div>
  );
};

export default Login;
